/**
 * Staff roles and what each may do. This is the single source for both the API
 * (which enforces it) and the web app (which only hides what a role cannot use).
 * See the role table in docs/atlas-control-panel-prd.md.
 */
export const STAFF_ROLES = ['auditor', 'admin', 'developer'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

export const STAFF_STATUSES = ['active', 'disabled'] as const;
export type StaffStatus = (typeof STAFF_STATUSES)[number];

export const PERMISSIONS = [
  'forms.read',
  'forms.create',
  'forms.edit',
  'forms.payment',
  'forms.review',
  'forms.delete',
  'runs.read',
  'runs.control',
  'final.edit',
  'final.review',
  'canvas.view',
  'email.manage',
  'credentials.reveal',
  'downloads.documents',
  'downloads.all_stages',
  'stages.details',
  'staff.manage_auditors',
  'staff.manage_all',
  'activity.read',
  'settings.manage',
  'errors.read',
  // Our products Brain proposes first (developers only).
  'catalog.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const AUDITOR: readonly Permission[] = [
  'forms.read',
  'forms.edit',
  'forms.payment',
  'forms.review',
  'runs.read',
  'runs.control',
  'final.edit',
  'final.review',
  'canvas.view',
  'email.manage',
  'credentials.reveal',
  'downloads.documents',
  // Everyone sees the activity feed; `activityRolesFor` decides whose.
  'activity.read',
];

const ADMIN: readonly Permission[] = [
  ...AUDITOR,
  'staff.manage_auditors',
  // A form entered by hand, for a custom run that did not come through intake.
  'forms.create',
];

export const ROLE_PERMISSIONS: Record<StaffRole, ReadonlySet<Permission>> = {
  auditor: new Set(AUDITOR),
  admin: new Set(ADMIN),
  // Developers have full access to the whole panel.
  developer: new Set(PERMISSIONS),
};

export function can(role: StaffRole | null | undefined, permission: Permission): boolean {
  return role ? ROLE_PERMISSIONS[role].has(permission) : false;
}

export function permissionsOf(role: StaffRole): Permission[] {
  return [...ROLE_PERMISSIONS[role]];
}

/** Which roles an actor may create or manage accounts for. */
export function manageableRoles(role: StaffRole): StaffRole[] {
  if (role === 'developer') return [...STAFF_ROLES];
  if (role === 'admin') return ['auditor'];
  return [];
}

/**
 * Whose actions a role may see in the activity feed: auditors see auditors,
 * admins see admins and auditors, developers see everyone.
 */
export function activityRolesFor(role: StaffRole): StaffRole[] {
  if (role === 'developer') return [...STAFF_ROLES];
  if (role === 'admin') return ['auditor', 'admin'];
  return ['auditor'];
}

export function isStaffRole(v: unknown): v is StaffRole {
  return typeof v === 'string' && (STAFF_ROLES as readonly string[]).includes(v);
}
